import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { clsx } from 'clsx';
import {
  shouldReduceMotion,
  viewportConfig,
} from '@/lib/animations/optimized-animations';

export interface NumberMetricProps {
  /** Target value to count up to. */
  value: number;
  /** Optional leading symbol, e.g. "€". */
  prefix?: string;
  /** Optional trailing symbol/word, e.g. "+", "%", " Formate". */
  suffix?: string;
  /** Caption rendered beneath the number (already translated). */
  label: string;
  /** Count-up duration in milliseconds. */
  durationMs?: number;
  /** Optional class names merged onto the root element. */
  className?: string;
}

/**
 * Animated count-up metric. Counts from 0 to `value` once scrolled into view.
 * Respects reduced-motion: shows the final value instantly with no tween.
 */
export function NumberMetric({
  value,
  prefix,
  suffix,
  label,
  durationMs = 1600,
  className,
}: NumberMetricProps) {
  const reduce = shouldReduceMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: viewportConfig.once,
    amount: viewportConfig.amount,
  });
  const [display, setDisplay] = useState<number>(reduce ? value : 0);

  useEffect(() => {
    if (reduce) {
      setDisplay(value);
      return;
    }
    if (!inView) return;

    let frame = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / durationMs, 1);
      // easeOutCubic for a natural settle.
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setDisplay(value);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduce, value, durationMs]);

  const decimals = Number.isInteger(value) ? 0 : 1;
  const formatted = display.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <div ref={ref} className={clsx('flex flex-col items-center text-center', className)}>
      <span
        className="text-[var(--text)] text-4xl font-bold tabular-nums leading-none sm:text-5xl"
        aria-label={`${prefix ?? ''}${value}${suffix ?? ''}`}
      >
        {prefix ? <span className="text-[var(--cyan)]">{prefix}</span> : null}
        <span>{formatted}</span>
        {suffix ? <span className="text-[var(--cyan)]">{suffix}</span> : null}
      </span>
      <span className="mt-2 text-xs font-medium uppercase tracking-wider text-[var(--muted-2)]">
        {label}
      </span>
    </div>
  );
}
